
import { useState, useEffect } from "react";
import { toast } from "sonner";

export type LocationState = {
  latitude: number | null;
  longitude: number | null;
  error: string | null;
  isLoading: boolean;
  permission: PermissionState | null;
};

export type LocationOptions = {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
};

export function useUserLocation(options: LocationOptions = {}) {
  const [state, setState] = useState<LocationState>({
    latitude: null,
    longitude: null,
    error: null,
    isLoading: false,
    permission: null,
  });

  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
    ...options,
  };

  const requestPermission = async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      // Check if Geolocation is supported
      if (!navigator.geolocation) {
        throw new Error("Geolocation is not supported by your browser");
      }

      // Check permission status first
      const permissionStatus = await navigator.permissions.query({ name: "geolocation" as PermissionName });
      setState(prev => ({ ...prev, permission: permissionStatus.state }));
      
      // Listen for permission changes
      permissionStatus.addEventListener("change", () => {
        setState(prev => ({ ...prev, permission: permissionStatus.state }));
      });

      // Get current position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, defaultOptions);
      });

      setState({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        error: null,
        isLoading: false,
        permission: permissionStatus.state,
      });
      
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown location error";
      setState({ 
        latitude: null, 
        longitude: null, 
        error: errorMessage, 
        isLoading: false,
        permission: state.permission,
      });
      return null;
    }
  };

  const promptForLocation = async () => {
    // First show a toast explaining why we need location
    toast.info(
      "Location Permission Required", 
      { 
        description: "We need your location to verify you're at the venue when redeeming a voucher.",
        duration: 5000,
      }
    );
    
    return await requestPermission();
  };

  return {
    ...state,
    requestPermission,
    promptForLocation,
  };
}
